import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Referenced from the Privacy Policy §7 as xpert.company/legal/privacy-request.
// Email is the guaranteed channel; the form is a convenience that falls back
// to email if the intake API is unavailable.
export default function PrivacyRequestPage() {
  const [form, setForm] = useState({ email: "", request_type: "access", description: "" });
  const [state, setState] = useState("idle"); // idle | submitting | done | fallback
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setState("submitting");
    try {
      await axios.post(`${API}/api/legal/dsr`, {
        email: form.email,
        request_type: form.request_type,
        description: form.description,
      });
      setState("done");
    } catch {
      // Intake API unavailable (or captcha-gated) — email is always available.
      setState("fallback");
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      <main className="max-w-2xl mx-auto px-5 py-16">
        <Link to="/privacy" className="text-sm text-brand-green hover:text-brand-greenHover">← Privacy Policy</Link>
        <h1 className="font-display text-4xl mt-4 mb-3">Make a privacy request</h1>
        <p className="text-slateline-600 leading-relaxed mb-6">
          You can ask us to access, correct, delete, or port your personal data, or to object to or
          restrict certain processing, as described in our{" "}
          <Link to="/privacy" className="text-brand-green underline hover:no-underline">Privacy Policy</Link>.
          The fastest guaranteed channel is email — write to{" "}
          <a href="mailto:privacy@xpert.company" className="text-brand-green underline hover:no-underline">privacy@xpert.company</a>{" "}
          and we'll acknowledge and act within the statutory timeframe.
        </p>

        {state === "done" ? (
          <div className="border border-brand-green/30 bg-brand-greenPale rounded-md p-5 text-sm text-ink">
            Thanks — your request has been received. We'll email you at {form.email} to verify your identity and follow up.
          </div>
        ) : state === "fallback" ? (
          <div className="border border-slateline-300 bg-canvas-card rounded-md p-5 text-sm text-slateline-700">
            Our online form is temporarily unavailable. Please email your request to{" "}
            <a href="mailto:privacy@xpert.company" className="text-brand-green underline hover:no-underline">privacy@xpert.company</a>{" "}
            (include your request type and the email on your account) and we'll take it from there.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Your account email</label>
              <input type="email" required value={form.email} onChange={set("email")}
                     className="w-full border border-slateline-300 rounded-md px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Request type</label>
              <select value={form.request_type} onChange={set("request_type")}
                      className="w-full border border-slateline-300 rounded-md px-3 py-2 text-sm bg-white">
                <option value="access">Access my data</option>
                <option value="deletion">Delete my data</option>
                <option value="correction">Correct my data</option>
                <option value="portability">Export / port my data</option>
                <option value="objection">Object to / restrict processing</option>
                <option value="optout">Do not sell/share (opt out)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Details (optional)</label>
              <textarea rows={4} value={form.description} onChange={set("description")}
                        className="w-full border border-slateline-300 rounded-md px-3 py-2 text-sm bg-white" />
            </div>
            <Button type="submit" disabled={state === "submitting"}>
              {state === "submitting" ? "Submitting…" : "Submit request"}
            </Button>
          </form>
        )}
      </main>
      <MarketingFooter />
    </div>
  );
}
